/**
 * Allowlist of `?returnFormId=` values that override the CTA link on a content page.
 * Used by Sams-flow terms pages so that a single Contentful entry can serve as both
 * the pre-auth and post-auth terms page (one CTA returns to the Sams login flow, the
 * other returns to the ptz-us quote form via /quote). See ENG-16328.
 *
 * URLs match what the original duplicate entries had:
 *   - "sams"          → SCIS (was the customCtaLink on /rx-terms and /sams-gc-terms)
 *   - "quote-ptz-us"  → /quote (was the fallback on /rx-terms-2 and /sams-gc-terms-2,
 *                       which routes through middleware.ts so quoteId/tracking/Builder
 *                       cookies are preserved and the locale-correct quote app is hit)
 *
 * Keep this map small. The values are fixed strings — never user-supplied — so this
 * stays open-redirect safe.
 */
const RETURN_FORM_TARGETS: Record<string, () => string> = {
    sams: () => "https://scis.spotpet.com",
    "quote-ptz-us": () => "/quote"
};

/**
 * Resolves the URL for the site-wide CTA button.
 *
 * Precedence:
 *   1. `?returnFormId=<id>` query param if it matches a known form id.
 *   2. The page's `customCtaLink` from Contentful.
 *   3. `fallback` (defaults to "/quote").
 *
 * Unknown `returnFormId` values are ignored silently.
 */
export function resolveCtaLink(args: { customCtaLink?: string; returnFormId?: string | string[]; fallback?: string }): string {
    const formId = Array.isArray(args.returnFormId) ? args.returnFormId[0] : args.returnFormId;
    if (formId && RETURN_FORM_TARGETS[formId]) {
        return RETURN_FORM_TARGETS[formId]();
    }
    return args.customCtaLink || args.fallback || "/quote";
}
